// src/app/modules/Devices/components/EnrolModal.jsx
// Pick a staff member → enable registration → watch the device report the enrolment live.

import React, { useEffect, useMemo, useState } from 'react';
import { Fingerprint, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import { devicesApi, errorMessage } from '../api';
import { usePolling, useEvent } from '../utils';
import { DvModal, Button, Spinner } from './ui';

const POLL_MS = 2000;

const STATE_COPY = {
  waiting: { tone: 'waiting', title: 'Waiting for the fingerprint', body: 'Press the button on the device, then scan the same finger 3 times.' },
  waiting_after_failed: { tone: 'failed', title: 'That attempt didn’t work', body: 'Press the button again and scan the same finger 3 times.' },
  waiting_after_conflict: { tone: 'failed', title: 'Slot already in use', body: 'The sensor reused a slot linked to someone else. Remove that fingerprint, then try again.' },
  enrolled: { tone: 'success', title: 'Fingerprint enrolled', body: '' },
  expired: { tone: 'failed', title: 'Time ran out', body: 'No fingerprint was received in time. Start again when the person is at the device.' },
  cancelled: { tone: 'failed', title: 'Registration cancelled', body: '' },
};

export function EnrolModal({ show, onHide, orgId, device, onEnrolled }) {
  const [members, setMembers] = useState(null);
  const [membershipId, setMembershipId] = useState('');
  const [search, setSearch] = useState('');
  const [phase, setPhase] = useState('pick'); // pick | live
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!show) return;
    setPhase(device.registration_open ? 'live' : 'pick');
    setStatus(null);
    setSearch('');
    devicesApi.enrollableMembers(orgId)
      .then((r) => setMembers(r.members))
      .catch((err) => { setMembers([]); toast.error(errorMessage(err, 'Could not load staff')); });
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = useEvent(async () => {
    try {
      const s = await devicesApi.registrationStatus(orgId, device.device_id);
      setStatus(s);
      if (s.state === 'enrolled' && onEnrolled) onEnrolled();
    } catch (_) { /* transient — next poll retries */ }
  });
  const live = show && phase === 'live';
  usePolling(poll, POLL_MS, live && status?.state !== 'enrolled');
  usePolling(() => setNow(Date.now()), 1000, live && !!status?.registration_open);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (members || []).filter((m) => !q || [m.display_name, m.employee_id].some((v) => (v || '').toLowerCase().includes(q)));
  }, [members, search]);

  const start = async () => {
    if (!membershipId) return;
    setBusy(true);
    try {
      await devicesApi.enableRegistration(orgId, device.device_id, membershipId);
      setStatus(null);
      setPhase('live');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not enable registration'));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await devicesApi.cancelRegistration(orgId, device.device_id);
      onHide();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reg = status?.registration || {};
  const copy = STATE_COPY[status?.state] || STATE_COPY.waiting;
  const secondsLeft = reg.expires_at ? Math.max(0, Math.round((new Date(reg.expires_at) - now) / 1000)) : null;
  const deviceName = device.name || device.device_id;

  if (phase === 'pick') {
    return (
      <DvModal
        show={show}
        onHide={onHide}
        title="Enrol a fingerprint"
        subtitle={`On ${deviceName}`}
        footer={(
          <>
            <Button variant="ghost" onClick={onHide}>Cancel</Button>
            <Button variant="primary" icon={Fingerprint} onClick={start} loading={busy} disabled={!membershipId}>Start enrolment</Button>
          </>
        )}
      >
        <ol className="dv-steps">
          <li>Choose who is enrolling.</li>
          <li>Go to the device within {Math.round((device.settings?.registration_ttl_s || 300) / 60)} minutes and press its button.</li>
          <li>Scan the same finger 3 times when the screen asks.</li>
        </ol>
        {members === null ? (
          <div style={{ textAlign: 'center', padding: 16 }}><Spinner /></div>
        ) : members.length === 0 ? (
          <div className="dv-notice dv-notice--warning">
            <AlertTriangle size={16} />
            <span>No staff to enrol yet. Add staff or security members under <b>Members</b> first.</span>
          </div>
        ) : (
          <>
            {members.length > 8 && (
              <label className="dv-field">
                <span>Search</span>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or employee ID" />
              </label>
            )}
            <label className="dv-field">
              <span>Staff member</span>
              <select value={membershipId} onChange={(e) => setMembershipId(e.target.value)}>
                <option value="">Select a person…</option>
                {filtered.map((m) => (
                  <option key={m.membership_id} value={m.membership_id}>
                    {m.display_name}{m.employee_id ? ` · ${m.employee_id}` : ''}{m.fingerprints ? ` (${m.fingerprints} enrolled)` : ''}
                  </option>
                ))}
              </select>
              <small>People can enrol more than one finger — each is stored separately.</small>
            </label>
          </>
        )}
      </DvModal>
    );
  }

  const done = status?.state === 'enrolled';
  return (
    <DvModal
      show={show}
      onHide={onHide}
      title="Enrol a fingerprint"
      subtitle={`On ${deviceName}`}
      footer={done ? (
        <>
          <Button onClick={() => { setPhase('pick'); setMembershipId(''); }}>Enrol another</Button>
          <Button variant="primary" onClick={onHide}>Done</Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={onHide}>Close</Button>
          {status?.registration_open && <Button variant="danger" onClick={cancel} loading={busy}>Cancel registration</Button>}
          {['expired', 'cancelled'].includes(status?.state) && <Button variant="primary" onClick={() => setPhase('pick')}>Start again</Button>}
        </>
      )}
    >
      <div className="dv-enrol-status">
        <span className={`dv-enrol-status__ring ${copy.tone}`}>
          {done ? <CheckCircle size={30} /> : copy.tone === 'failed' ? <AlertTriangle size={30} /> : <Fingerprint size={32} />}
        </span>
        <h3>{copy.title}</h3>
        {reg.display_name && <p>For <b>{reg.display_name}</b></p>}
        {copy.body && <p>{copy.body}</p>}
        {reg.last_reason && !done && <p>Device reported: {reg.last_reason}</p>}
        {done && status.enrollment && <p>Stored in fingerprint slot <b>#{status.enrollment.uid}</b>. They can clock in now.</p>}
        {!done && status?.registration_open && secondsLeft !== null && (
          <span className="dv-countdown"><Clock size={13} style={{ verticalAlign: -2 }} /> {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')} left</span>
        )}
        {!status && <Spinner />}
      </div>
    </DvModal>
  );
}

export default EnrolModal;
